import { createContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import authService from '@services/auth.service';
import type { User, AuthContextType } from '@/types';

export const AuthContext = createContext<AuthContextType | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps): JSX.Element => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const loadUser = useCallback(async (): Promise<void> => {
    try {
      setLoading(true);
      setError(null);
      const userData = await authService.getUser();
      setUser(userData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
      // Set anonymous user on error
      setUser({
        email: null,
        roles: [],
        accessLevel: 'anonymous',
        isAuthenticated: false,
        isProjectMember: false,
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUser();
  }, [loadUser]);

  const login = useCallback((): void => {
    authService.login();
  }, []);

  const logout = useCallback((): void => {
    authService.logout();
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        error,
        login,
        logout,
        refreshUser: loadUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
